(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(any-pointer: fine)');
  const sectionSelector = '#MainContent > .shopify-section';
  const revealSelector = [
    sectionSelector,
    '.product-card',
    'product-card',
    '.collection-card',
    '.resource-card',
    '.mushq-arrivals__card',
    '.sam-product-carousel__card',
    '.sam-ref-products__card',
    '.sam-ref-home-product',
    '.sam-category-circles__item',
    '.sam-category-tiles__card',
    '.sam-promo-banners__banner',
    '.custom-faq-accordion__item',
    '.samraajya-contact-form__message',
    '.samraajya-contact-form__item',
    '.samraajya-support-strip__item',
    '.sa-features__item',
    '.sa-values__item',
    '.sam-about-values__item',
    '.sam-about-stats__item',
    'details'
  ].join(',');
  const interactiveSelector = [
    '.product-card',
    'product-card',
    '.collection-card',
    '.resource-card',
    '.mushq-arrivals__card',
    '.sam-product-carousel__card',
    '.sam-ref-products__card',
    '.sam-ref-home-product',
    '.sam-category-circles__item',
    '.sam-category-tiles__card',
    '.sam-promo-banners__banner',
    '.custom-faq-accordion__item',
    '.samraajya-contact-form__message',
    '.samraajya-contact-form__item',
    '.samraajya-support-strip__item',
    '.sa-features__item',
    '.sa-values__item',
    '.sam-about-values__item',
    '.sam-about-stats__item',
    '.samraajya-footer__payment-card',
    '.button',
    '.shopify-payment-button__button',
    'button[type="submit"]',
    'a[class*="button"]',
    '[role="button"]'
  ].join(',');
  const magneticSelector = [
    '.button',
    '.add-to-cart-button',
    '.shopify-payment-button__button',
    'button[type="submit"]',
    'a[class*="button"]',
    '.sa-hero__button',
    '.mushq-arrivals__view-all',
    '.sam-ref-home__button',
    '.sam-ref-banner__button',
    '[role="button"]'
  ].join(',');
  const parallaxSelector = [
    '#MainContent > .shopify-section:nth-child(-n + 2) .sa-hero__image',
    '#MainContent > .shopify-section:nth-child(-n + 2) .sam-ref-hero__image',
    '#MainContent > .shopify-section:nth-child(-n + 2) .samraajya-hero__image',
    '#MainContent > .shopify-section:nth-child(-n + 2) .sam-ref-banner__image',
    '#MainContent > .shopify-section:nth-child(-n + 2) .hero img',
    '#MainContent > .shopify-section:nth-child(-n + 2) .banner__media img',
    '#MainContent > .shopify-section:nth-child(-n + 2) slideshow-component img',
    '#MainContent > .shopify-section:nth-child(-n + 2) .slideshow__media img',
    '#MainContent > .shopify-section:nth-child(-n + 2) .media-with-content img'
  ].join(',');

  let revealObserver = null;
  let mutationObserver = null;
  let mutationTimer = 0;
  let activeElement = null;
  let pointerFrame = 0;
  let scrollFrame = 0;
  let lastScrollTop = 0;
  let parallaxItems = [];
  let magneticElement = null;

  function body() {
    return document.body;
  }

  function enabled() {
    return body()?.dataset.samraajyaEffects === 'true';
  }

  function feature(name) {
    return body()?.dataset[name] === 'true';
  }

  function scrollTargets() {
    return [document.querySelector('.page-wrapper'), document.scrollingElement || document.documentElement].filter(Boolean);
  }

  function primaryScrollTarget() {
    const wrapper = document.querySelector('.page-wrapper');

    if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 2) {
      return wrapper;
    }

    return document.scrollingElement || document.documentElement;
  }

  function scrollTopFor(target) {
    if (target === document.scrollingElement || target === document.documentElement) {
      return window.scrollY || target.scrollTop || 0;
    }

    return target.scrollTop || 0;
  }

  function readPixelVar(name, fallback) {
    const value = window.getComputedStyle(document.body).getPropertyValue(name).trim();
    const parsed = Number.parseFloat(value);

    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function updateParallax() {
    if (!feature('samraajyaHeroParallax') || reduceMotion.matches || !parallaxItems.length) return;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const strength = readPixelVar('--sa-parallax-strength', 16);

    parallaxItems.forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (rect.bottom < -120 || rect.top > viewportHeight + 120) return;

      const center = rect.top + rect.height / 2;
      const progress = (center - viewportHeight / 2) / Math.max(viewportHeight, 1);
      const y = Math.max(strength * -1, Math.min(strength, progress * strength * -1));
      element.style.setProperty('--sa-parallax-y', `${y.toFixed(2)}px`);
    });
  }

  function updateScrollState() {
    scrollFrame = 0;

    const target = primaryScrollTarget();
    const maxScroll = Math.max(1, target.scrollHeight - target.clientHeight);
    const top = scrollTopFor(target);
    const progress = Math.min(1, Math.max(0, top / maxScroll));

    root.style.setProperty('--sa-scroll-progress', progress.toFixed(4));
    root.classList.toggle('samraajya-scrolled', top > 18);
    root.classList.toggle('samraajya-backtop-visible', top > 520);

    if (Math.abs(top - lastScrollTop) > 4) {
      root.dataset.saScrollDirection = top > lastScrollTop ? 'down' : 'up';
      lastScrollTop = top;
    }

    updateParallax();
  }

  function requestScrollUpdate() {
    if (scrollFrame) return;
    scrollFrame = window.requestAnimationFrame(updateScrollState);
  }

  function bindScrollState() {
    for (const target of scrollTargets()) {
      target.addEventListener('scroll', requestScrollUpdate, { passive: true });
    }

    window.addEventListener('resize', requestScrollUpdate, { passive: true });
    requestScrollUpdate();
  }

  function ensureProgressBar() {
    if (!feature('samraajyaScrollProgress')) return;
    if (document.querySelector('.samraajya-scroll-progress')) return;

    const progress = document.createElement('div');
    progress.className = 'samraajya-scroll-progress';
    progress.setAttribute('aria-hidden', 'true');
    document.body.prepend(progress);
  }

  function ensureBackToTop() {
    if (!feature('samraajyaBackToTop')) return;
    if (document.querySelector('.samraajya-back-to-top')) return;

    const button = document.createElement('button');
    button.className = 'samraajya-back-to-top';
    button.type = 'button';
    button.setAttribute('aria-label', 'Back to top');
    button.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 19V5"></path><path d="m6 11 6-6 6 6"></path></svg>';
    button.addEventListener('click', () => {
      const target = primaryScrollTarget();
      const behavior = reduceMotion.matches ? 'auto' : 'smooth';

      if (target === document.scrollingElement || target === document.documentElement) {
        window.scrollTo({ top: 0, behavior });
      } else {
        target.scrollTo({ top: 0, behavior });
      }
    });

    document.body.append(button);
  }

  function isAlreadyVisible(element) {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    return rect.top < viewportHeight * 0.96 && rect.bottom > 0;
  }

  function revealElements(scope = document) {
    if (!feature('samraajyaReveal')) return;

    const candidates = [];

    if (scope instanceof Element && scope.matches(revealSelector)) {
      candidates.push(scope);
    }

    if (scope.querySelectorAll) {
      candidates.push(...scope.querySelectorAll(revealSelector));
    }

    candidates.forEach((element, index) => {
      if (element.dataset.saReveal === 'true') return;

      element.dataset.saReveal = 'true';
      element.style.setProperty('--sa-reveal-delay', `${Math.min(index % 8, 6) * 22}ms`);

      if (reduceMotion.matches || isAlreadyVisible(element)) {
        element.classList.add('sa-in-view');
      }

      if (revealObserver) {
        revealObserver.observe(element);
      }
    });
  }

  function setupReveals() {
    if (!feature('samraajyaReveal')) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      revealElements();
      document.querySelectorAll('[data-sa-reveal]').forEach((element) => element.classList.add('sa-in-view'));
      return;
    }

    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('sa-in-view');
          revealObserver.unobserve(entry.target);
        });
      },
      {
        root: null,
        rootMargin: '0px 0px 16% 0px',
        threshold: 0.04
      }
    );

    revealElements();

    mutationObserver = new MutationObserver((mutations) => {
      window.clearTimeout(mutationTimer);
      mutationTimer = window.setTimeout(() => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element) {
              revealElements(node);
              setupHeroParallax(node);
            }
          });
        });
      }, 80);
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('shopify:section:load', (event) => {
      if (event.target instanceof Element) {
        revealElements(event.target);
        setupHeroParallax(event.target);
      }
    });
  }

  function clearPointerElement() {
    if (!activeElement) return;

    activeElement.classList.remove('samraajya-pointer-active');
    activeElement = null;
  }

  function bindPointerEffects() {
    if (!feature('samraajyaCardHover') || reduceMotion.matches || !finePointer.matches) return;

    document.addEventListener(
      'pointermove',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const element = target ? target.closest(interactiveSelector) : null;

        if (!element) {
          clearPointerElement();
          return;
        }

        if (pointerFrame) return;

        pointerFrame = window.requestAnimationFrame(() => {
          pointerFrame = 0;

          if (activeElement && activeElement !== element) {
            clearPointerElement();
          }

          activeElement = element;
          element.classList.add('samraajya-pointer-active');
        });
      },
      { passive: true }
    );

    document.addEventListener('pointerout', (event) => {
      if (
        !activeElement ||
        (event.relatedTarget instanceof Node && activeElement.contains(event.relatedTarget))
      ) {
        return;
      }

      clearPointerElement();
    });
  }


  function setupHeroParallax(scope = document) {
    if (!feature('samraajyaHeroParallax') || reduceMotion.matches) return;

    const candidates = [];
    if (scope instanceof Element && scope.matches(parallaxSelector)) {
      candidates.push(scope);
    }

    if (scope.querySelectorAll) {
      candidates.push(...scope.querySelectorAll(parallaxSelector));
    }

    candidates.forEach((element) => {
      if (!(element instanceof HTMLElement) || element.dataset.saParallax === 'true') return;
      element.dataset.saParallax = 'true';
      parallaxItems.push(element);
    });

    updateParallax();
  }

  function ensureSparkles() {
    if (!feature('samraajyaSparkles') || reduceMotion.matches) return;
    if (document.querySelector('.samraajya-sparkle-layer')) return;

    const layer = document.createElement('div');
    layer.className = 'samraajya-sparkle-layer';
    layer.setAttribute('aria-hidden', 'true');

    for (let index = 0; index < 12; index += 1) {
      layer.append(document.createElement('span'));
    }

    document.body.append(layer);
  }

  function bindMagneticButtons() {
    if (!feature('samraajyaMagneticButtons') || reduceMotion.matches || !finePointer.matches) return;

    document.addEventListener(
      'pointermove',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const element = target ? target.closest(magneticSelector) : null;

        if (!(element instanceof HTMLElement)) {
          if (magneticElement) {
            magneticElement.classList.remove('samraajya-magnetic-active');
            magneticElement.classList.add('samraajya-magnetic-reset');
            magneticElement.style.setProperty('--sa-magnetic-x', '0px');
            magneticElement.style.setProperty('--sa-magnetic-y', '0px');
            magneticElement = null;
          }
          return;
        }

        const rect = element.getBoundingClientRect();
        const strength = readPixelVar('--sa-magnetic-strength', 6);
        const x = ((event.clientX - rect.left) / Math.max(rect.width, 1) - 0.5) * strength;
        const y = ((event.clientY - rect.top) / Math.max(rect.height, 1) - 0.5) * strength;

        if (magneticElement && magneticElement !== element) {
          magneticElement.classList.remove('samraajya-magnetic-active');
          magneticElement.style.setProperty('--sa-magnetic-x', '0px');
          magneticElement.style.setProperty('--sa-magnetic-y', '0px');
        }

        magneticElement = element;
        element.classList.remove('samraajya-magnetic-reset');
        element.classList.add('samraajya-magnetic-active');
        element.style.setProperty('--sa-magnetic-x', `${x.toFixed(2)}px`);
        element.style.setProperty('--sa-magnetic-y', `${y.toFixed(2)}px`);
      },
      { passive: true }
    );

    document.addEventListener('pointerout', (event) => {
      if (!(magneticElement instanceof HTMLElement)) return;
      if (event.relatedTarget instanceof Node && magneticElement.contains(event.relatedTarget)) return;

      const element = magneticElement;
      element.classList.remove('samraajya-magnetic-active');
      element.classList.add('samraajya-magnetic-reset');
      element.style.setProperty('--sa-magnetic-x', '0px');
      element.style.setProperty('--sa-magnetic-y', '0px');
      window.setTimeout(() => element.classList.remove('samraajya-magnetic-reset'), 430);
      magneticElement = null;
    });
  }

  function bindTapFeedback() {
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const element = target ? target.closest(interactiveSelector) : null;
        if (!element) return;

        element.classList.remove('samraajya-tapped');
        void element.offsetWidth;
        element.classList.add('samraajya-tapped');
        window.setTimeout(() => element.classList.remove('samraajya-tapped'), 380);
      },
      { capture: true }
    );
  }

  function init() {
    if (!document.body || !enabled()) return;

    root.classList.add('samraajya-effects-ready');
    ensureProgressBar();
    ensureBackToTop();
    ensureSparkles();
    setupHeroParallax();
    bindScrollState();
    setupReveals();
    bindPointerEffects();
    bindMagneticButtons();
    bindTapFeedback();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
